import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { ColDef, GridOptions } from "ag-grid-community";
import { TemplateRendererComponent } from '@app/shared/components/tables/ag-grid-cells/template-renderer/template-renderer.component';
import { RecordDetailsFlyoutComponent, RecordDetailField } from '@app/shared/components/record-details-flyout/record-details-flyout.component';
import { UiButtonDirective } from '@template-ui/directives/ui-button.directive';
import { caseCountValueGetter, formatRecordDate, formatRecordLabel } from './case-context-record-grid-columns';

import {
  InmateOffenseRecord,
  RecordPanelState,
  RecordPanelStateCopy,
} from "@app/case-context/models/inmate-offense-record.model";

@Component({
  selector: "phms-case-context-record-grid",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, RecordDetailsFlyoutComponent, UiButtonDirective],
  templateUrl: "./case-context-record-grid.component.html",
  styleUrl: "./case-context-record-grid.component.scss",
})
export class CaseContextRecordGridComponent implements OnChanges, OnInit {
  @ViewChild('recordActions', { static: true }) private recordActions!: TemplateRef<unknown>;
  @Input({ required: true }) heading = "";
  @Input({ required: true }) columnDefs: ColDef[] = [];
  @Input() records: InmateOffenseRecord[] = [];
  @Input() state: RecordPanelState = "ready";
  @Input() stateCopy: RecordPanelStateCopy = {};
  @Input() itemLabelSingular = "record";
  @Input() itemLabelPlural = "records";
  @Input() pageSizes: number[] = [10, 20, 50];
  @Input() rowHeight = 34;
  @Input() noRowsHeight = "120px";
  @Input() agGridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 10,
    suppressPaginationPanel: true,
    enableCellTextSelection: true,
  };

  readonly defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    floatingFilter: false,
    suppressHeaderFilterButton: false,
    resizable: true,
    suppressMovable: true,
    minWidth: 80,
    cellStyle: {
      alignItems: "center",
      display: "flex",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };

  viewColumns: ColDef[] = [];
  selected: InmateOffenseRecord | null = null;
  get detailTitle(): string {
    return this.itemLabelSingular.charAt(0).toUpperCase() + this.itemLabelSingular.slice(1) + ' details';
  }

  ngOnInit(): void { this.buildColumns(); }
  ngOnChanges(): void {
    this.buildColumns();
    if (this.state !== 'ready' || !this.records.includes(this.selected!)) this.selected = null;
  }
  private buildColumns(): void {
    this.viewColumns = [...this.columnDefs.map(column => column.field === 'recordCode'
      ? { ...column, pinned: 'left' as const, lockPinned: true }
      : column), {
      colId: 'recordDetails', headerName: 'Details', width: 85, minWidth: 80, maxWidth: 95,
      pinned: 'right', lockPinned: true,
      sortable: false, filter: false, resizable: false,
      cellRenderer: TemplateRendererComponent,
      cellRendererParams: { ngTemplate: this.recordActions },
    }];
  }
  openDetails(record: InmateOffenseRecord): void {
    if (this.state === 'ready' && this.records.includes(record)) this.selected = record;
  }
  recordTitle(record: InmateOffenseRecord): string {
    return record.recordCode || record.recordLabel || this.itemLabelSingular;
  }
  detailFields(record: InmateOffenseRecord): RecordDetailField[] {
    const fields: RecordDetailField[] = [
      { label: 'Code', value: record.recordCode },
      { label: 'Record', value: record.recordLabel },
      { label: 'Date', value: formatRecordDate(record.eventDate) },
      { label: 'Record status', value: formatRecordLabel(record.recordStatusCode) },
    ];
    if (record.caseNumber?.trim() || record.countNumber != null)
      fields.push({ label: 'Case / count', value: caseCountValueGetter({ data: record } as Parameters<typeof caseCountValueGetter>[0]) });
    if (record.statuteReference?.trim()) fields.push({ label: 'Statute', value: record.statuteReference });
    if (record.dispositionCode?.trim()) fields.push({ label: 'Disposition', value: formatRecordLabel(record.dispositionCode) });
    if (record.dispositionDate) fields.push({ label: 'Disposition date', value: formatRecordDate(record.dispositionDate) });
    if (record.severityCode?.trim()) fields.push({ label: 'Severity', value: formatRecordLabel(record.severityCode) });
    return fields;
  }

  get loadingText(): string {
    return this.stateCopy.loadingText ?? "Loading records...";
  }

  get emptyText(): string {
    return this.stateCopy.emptyText ?? "No records to display.";
  }

  get errorText(): string {
    return this.stateCopy.errorText ?? "Records are temporarily unavailable.";
  }

}
